import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronRight, Search, Star, X } from 'lucide-react';
import { PropRow } from '../activity-drawer/ui/PropRow';
import { useInterestCategoryStore } from '../../../lib/stores/interestCategoryStore';
import type { InterestCategoryNode } from '../../../lib/types';

interface InterestTreeChipRowProps {
  selected: string[];
  onChange: (ids: string[]) => void;
  /** Tooltip per chip selezionato (es. data di assegnazione), per id. */
  hints?: Record<string, string>;
  /** Chip già assegnati assenti dallo store (id disattivato, altra org): mai id grezzi. */
  extraOptions?: { value: string; label: string; color?: string | null }[];
  label?: string;
  disabled?: boolean;
}

/** Filtra l'albero per ricerca: un nodo resta se combacia lui o un discendente. */
function filterTree(nodes: InterestCategoryNode[], query: string): InterestCategoryNode[] {
  const lower = query.toLowerCase();
  const filterNode = (node: InterestCategoryNode): InterestCategoryNode | null => {
    if (!node.is_active) return null;
    const children = node.children.map(filterNode).filter((n): n is InterestCategoryNode => n !== null);
    if (!query || node.name.toLowerCase().includes(lower) || children.length > 0) {
      return { ...node, children };
    }
    return null;
  };
  return nodes.map(filterNode).filter((n): n is InterestCategoryNode => n !== null);
}

const collectIds = (nodes: InterestCategoryNode[]): string[] =>
  nodes.flatMap((n) => [n.id, ...collectIds(n.children)]);

/**
 * Riga "Interessi" a chip con selezione dall'ALBERO delle categorie (ricerca +
 * gerarchia espandibile, stessa logica di InterestCategoryTreeView nelle
 * impostazioni): selezionare un nodo aggiunge anche gli antenati, deselezionarlo
 * rimuove i discendenti. Sostituisce la tendina piatta di MultiChipRow.
 */
export function InterestTreeChipRow({ selected, onChange, hints, extraOptions, label = 'Interessi', disabled }: InterestTreeChipRowProps) {
  const { categories, categoryTree, fetchCategories, getDescendantIds, getAncestorIds } = useInterestCategoryStore();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Larghezza adattiva: il popover parte da 288px ma si stringe se il trigger
  // è vicino al bordo destro del viewport (pannello mobile), dove l'overflow
  // del drawer taglierebbe tutto ciò che sborda
  const [popWidth, setPopWidth] = useState(288);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (categories.length === 0) fetchCategories();
  }, [categories.length, fetchCategories]);

  // Chiusura al click fuori
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const openPopover = () => {
    if (disabled) return;
    // Parte con gli antenati delle selezioni espansi, così le spunte sono visibili
    const toExpand = new Set<string>();
    selected.forEach((id) => getAncestorIds(id).forEach((a) => toExpand.add(a)));
    setExpandedIds(toExpand);
    const left = containerRef.current?.getBoundingClientRect().left ?? 0;
    setPopWidth(Math.max(120, Math.min(288, window.innerWidth - left - 12)));
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  const filteredTree = useMemo(() => filterTree(categoryTree, search.trim()), [categoryTree, search]);
  // Durante la ricerca l'albero filtrato è tutto espanso (i match possono essere in profondità)
  const searchExpanded = useMemo(
    () => (search.trim() ? new Set(collectIds(filteredTree)) : null),
    [filteredTree, search],
  );
  const effectiveExpanded = searchExpanded ?? expandedIds;

  const toggleSelect = (id: string) => {
    if (selected.includes(id)) {
      const descendants = getDescendantIds(id);
      onChange(selected.filter((x) => x !== id && !descendants.includes(x)));
    } else {
      onChange([...new Set([...selected, id, ...getAncestorIds(id)])]);
    }
  };

  const removeChip = (id: string) => {
    const descendants = getDescendantIds(id);
    onChange(selected.filter((x) => x !== id && !descendants.includes(x)));
  };

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Label/colore dei chip: store + eventuali assegnati fuori store
  const byId = useMemo(() => {
    const map = new Map<string, { label: string; color?: string | null }>();
    categories.forEach((c: any) => map.set(c.id, { label: c.name, color: c.color }));
    (extraOptions || []).forEach((o) => { if (!map.has(o.value)) map.set(o.value, { label: o.label, color: o.color }); });
    return map;
  }, [categories, extraOptions]);

  const TreeNode = ({ node, level }: { node: InterestCategoryNode; level: number }) => {
    const isSelected = selected.includes(node.id);
    const hasChildren = node.children.length > 0;
    const isExpanded = effectiveExpanded.has(node.id);
    return (
      <div>
        <button
          type="button"
          onClick={() => toggleSelect(node.id)}
          className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06]"
          style={{ paddingLeft: `${level * 14 + 6}px` }}
        >
          {hasChildren ? (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => toggleExpand(node.id, e)}
              className="rounded p-0.5 hover:bg-[rgba(55,53,47,0.1)] dark:hover:bg-white/[0.1]"
            >
              <ChevronRight className={`h-3 w-3 text-[#787774] transition-transform ${isExpanded ? 'rotate-90' : ''}`} strokeWidth={1.5} />
            </span>
          ) : (
            <span className="w-4 flex-shrink-0" />
          )}
          <span
            className={`flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded border ${
              isSelected ? 'border-[#2383E2] bg-[#2383E2]' : 'border-[rgba(55,53,47,0.3)] dark:border-gray-600'
            }`}
          >
            {isSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
          </span>
          <span className="h-2 w-2 flex-shrink-0 rounded-sm" style={{ backgroundColor: node.color || '#3B82F6' }} />
          <span className={`min-w-0 flex-1 truncate text-[13px] ${isSelected ? 'font-medium text-[#37352F] dark:text-white' : 'text-[#37352F] dark:text-gray-200'}`}>
            {node.name}
          </span>
          {hasChildren && (
            <span className="flex-shrink-0 rounded bg-[rgba(55,53,47,0.06)] px-1 text-[10px] text-[#787774] dark:bg-white/[0.08] dark:text-gray-400">
              {node.children.length}
            </span>
          )}
        </button>
        {hasChildren && isExpanded && node.children.map((child) => (
          <TreeNode key={child.id} node={child} level={level + 1} />
        ))}
      </div>
    );
  };

  return (
    <PropRow icon={<Star className="h-3 w-3" strokeWidth={1.5} />} label={label} wrap>
      {selected.map((v) => {
        const opt = byId.get(v);
        if (!opt) return null;
        return (
          <span
            key={v}
            title={hints?.[v]}
            className="group/chip inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11.5px] font-medium"
            style={{
              backgroundColor: opt.color ? `${opt.color}20` : 'rgba(55,53,47,0.08)',
              color: opt.color || undefined,
            }}
          >
            <span className="text-[#37352F] dark:text-white">{opt.label}</span>
            {!disabled && (
              <button
                type="button"
                aria-label={`Rimuovi ${opt.label}`}
                onClick={(e) => { e.stopPropagation(); removeChip(v); }}
                className="opacity-40 hover:opacity-100"
              >
                <X className="h-3 w-3" strokeWidth={1.5} />
              </button>
            )}
          </span>
        );
      })}

      <div ref={containerRef} className="relative min-w-[110px] flex-1">
        <button
          type="button"
          onClick={open ? () => { setOpen(false); setSearch(''); } : openPopover}
          disabled={disabled}
          className="w-full text-left text-[13px] text-[rgba(55,53,47,0.45)] hover:text-[rgba(55,53,47,0.65)] dark:text-gray-500 dark:hover:text-gray-400"
        >
          Aggiungi interesse…
        </button>

        {open && (
          <div
            style={{ width: popWidth }}
            className="absolute left-0 top-full z-50 mt-1 rounded-md border border-[rgba(55,53,47,0.09)] bg-white shadow-lg dark:border-white/10 dark:bg-gray-800"
            onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); setSearch(''); } }}
          >
            <div className="border-b border-[rgba(55,53,47,0.06)] p-1.5 dark:border-white/[0.06]">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[rgba(55,53,47,0.35)] dark:text-gray-500" strokeWidth={1.5} />
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cerca interesse…"
                  className="w-full rounded border border-[rgba(55,53,47,0.09)] bg-[rgba(55,53,47,0.03)] py-1 pl-7 pr-2 text-[13px] text-[#37352F] placeholder-[rgba(55,53,47,0.35)] focus:outline-none focus:ring-1 focus:ring-[#2383E2] dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:placeholder-gray-500"
                />
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto p-1">
              {filteredTree.length === 0 ? (
                <p className="px-2 py-2 text-[12px] text-[rgba(55,53,47,0.45)] dark:text-gray-500">
                  {search ? 'Nessun risultato' : 'Nessuna categoria disponibile'}
                </p>
              ) : (
                filteredTree.map((node) => <TreeNode key={node.id} node={node} level={0} />)
              )}
            </div>
            {selected.length > 0 && (
              <div className="border-t border-[rgba(55,53,47,0.06)] px-2.5 py-1.5 text-[11px] text-[rgba(55,53,47,0.5)] dark:border-white/[0.06] dark:text-gray-400">
                {selected.length} selezionat{selected.length === 1 ? 'o' : 'i'}
              </div>
            )}
          </div>
        )}
      </div>
    </PropRow>
  );
}
